import React, { useState } from 'react';
import { Tooltip } from 'antd';
import { Metric, ProductPPG, PromoConfigDictionary } from 'types/tradePromoOptimization';
import { formatNumberWithCommas } from 'utils/helperMethods';
import { KPIItem } from '../KPISelector';
import { colorConstants } from 'config/colorConstants';
import { InfoCircleOutlined } from '@ant-design/icons';
import EditableMetricInput from './EditableMetricInput';

type MetricCellProps = {
    pm: ProductPPG;
    headerKPIs: KPIItem[];
    promoConfigDictionary: PromoConfigDictionary;
    isInputDisabled: (metricId: number) => boolean;
    combinedViewMode: boolean;
    editingCell: { metricId: number; ppgName: string } | null;
    setEditingCell: (v: any) => void;
    editValue: string;
    setEditValue: (v: string) => void;
    isValidMetricInput: (value: string, metricConfig: any, setWarningMessage: (msg: string) => void) => boolean;
    handleInputChange: (metricId: number, ppgName: string, value: string) => void;
    metricType: 'spendDetails' | 'metrics';
};

const MetricCell: React.FC<MetricCellProps> = ({
    pm,
    headerKPIs,
    promoConfigDictionary,
    isInputDisabled,
    combinedViewMode,
    editingCell,
    setEditingCell,
    editValue,
    setEditValue,
    isValidMetricInput,
    handleInputChange,
    metricType,
}) => {
    const [warningMessage, setWarningMessage] = useState('');

    return (
        <div>
            {headerKPIs
                ?.filter((metric) => metric.isSelected && !metric.isHidden)
                .map((headerMetric) => {
                    const metric = pm?.[metricType].find((m: Metric) => m.id.toString() === headerMetric.value);
                    if (!metric) return null;

                    const isDisabled = isInputDisabled(metric.id);
                    const isEditing = editingCell?.metricId === metric.id && editingCell?.ppgName === pm.name;
                    const type: 'promoMetrics' | 'spendMetrics' =
                        metricType === 'metrics' ? 'promoMetrics' : 'spendMetrics';
                    const metricConfig = promoConfigDictionary?.[type][metric.id];
                    const prefix = metricConfig?.preUnit;
                    const suffix = metricConfig?.postUnit;
                    const allowedDecimals = metricConfig?.allowedDecimals;

                    return (
                        <div key={metric.id} className="flex flex-col min-w-[200px] max-w-[200px] bg-white">
                            <div className="h-9 border-l border-r border-b font-xs border-backgroundGreyAlt1 flex items-center">
                                <div
                                    className={`flex justify-between items-center w-full h-full ${
                                        isEditing ? '' : 'px-2 py-1 '
                                    } ${combinedViewMode ? 'cursor-default' : 'cursor-pointer'} ${
                                        isDisabled ? 'text-backgroundGreyAlt2' : ''
                                    }`}
                                    tabIndex={0}
                                    onClick={() => {
                                        if (!isDisabled && !combinedViewMode) {
                                            setEditingCell({ metricId: metric.id, ppgName: pm.name });
                                            setEditValue(metric.value);
                                        }
                                    }}
                                >
                                    {isEditing ? (
                                        <EditableMetricInput
                                            value={editValue}
                                            warningMessage={warningMessage}
                                            isEditing={isEditing}
                                            onChange={(val) => {
                                                setEditValue(val);
                                                isValidMetricInput(val, metricConfig, setWarningMessage);
                                            }}
                                            onValidate={(val) =>
                                                isValidMetricInput(val, metricConfig, setWarningMessage)
                                            }
                                            onSubmit={() => handleInputChange(metric.id, pm.name, editValue)}
                                            onCancel={() => {
                                                setEditingCell(null);
                                                setEditValue('');
                                                setWarningMessage('');
                                            }}
                                        />
                                    ) : (
                                        <div className="flex justify-between items-center w-full">
                                            <div className="truncate w-full">
                                                {prefix}
                                                {formatNumberWithCommas(Number(metric.value), allowedDecimals)}
                                                {suffix}
                                            </div>

                                            {metricType === 'metrics' && (
                                                <span className="ml-1">
                                                    <Tooltip
                                                        placement="top"
                                                        color="white"
                                                        destroyTooltipOnHide
                                                        overlayStyle={{ borderRadius: 4 }}
                                                        overlayInnerStyle={{ color: colorConstants.darkGrey }}
                                                        title={metric.recomValue?.toString()}
                                                    >
                                                        {!isInputDisabled(metric.id) && (
                                                            <InfoCircleOutlined style={{ fontSize: '16px' }} />
                                                        )}
                                                    </Tooltip>
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
        </div>
    );
};

export default MetricCell;
